import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Copy, FileText, Search } from 'lucide-react';

import { logsApi } from '../../../api/logs.js';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { DataTable } from '../../../components/DataTable.jsx';
import { Pagination } from '../../../components/ui/Pagination.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Tabs } from '../../../components/ui/Tabs.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Modal } from '../../../components/ui/Modal.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Card } from '../../../components/ui/Card.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { fmt } from '../../../lib/formatters.js';
import { t } from '../../../lib/i18n.js';

function statusTone(code) {
  if (!code) return 'neutral';
  if (code >= 500) return 'danger';
  if (code >= 400) return 'warning';
  if (code >= 300) return 'info';
  return 'success';
}

function severityTone(s) {
  return s === 'critical' ? 'danger' : s === 'warning' ? 'warning' : s === 'notice' ? 'info' : 'neutral';
}

// Turn a technical dotted/underscored code (e.g. "ai.models.assignment.update")
// into a readable label ("Ai · Models · Assignment · Update") so non technical
// operators can read the audit trail. The raw code stays available on hover.
function humanizeCode(code) {
  if (!code) return '';
  return code
    .split(/[._-]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' · ');
}

const METHOD_TONES = { GET: 'info', POST: 'success', PUT: 'warning', PATCH: 'warning', DELETE: 'danger' };

function MethodBadge({ method }) {
  return <Badge tone={METHOD_TONES[method] || 'neutral'} className="font-mono">{method}</Badge>;
}

function RequestId({ value }) {
  if (!value) return <span className="text-ink-faint">-</span>;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Request ID copiato.');
    } catch {
      toast.error('Copia non riuscita.');
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      title={`Copia ${value}`}
      className="group inline-flex items-center gap-1 font-mono text-[11px] text-ink-muted transition-colors hover:text-ink"
    >
      <span>{value.slice(-12)}</span>
      <Copy className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
    </button>
  );
}

function DetailButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-ink-muted transition-colors hover:bg-surface-subtle hover:text-ink"
    >
      <FileText className="h-3.5 w-3.5" aria-hidden="true" />
      Dettagli
    </button>
  );
}

function FilterBar({ children }) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-surface-elevated p-4">
      {children}
    </div>
  );
}

function DetailModal({ open, onClose, title, payload }) {
  return (
    <Modal open={open} onClose={onClose} title={title} size="xl">
      <pre className="overflow-x-auto rounded-md bg-surface p-3 text-xs">
        {payload ? JSON.stringify(payload, null, 2) : '-'}
      </pre>
    </Modal>
  );
}

function RequestsTab() {
  const { items, isLoading, pagination, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['logs', 'requests'],
    fetcher: logsApi.listRequests
  });
  const [detail, setDetail] = useState(null);

  const columns = [
    { key: 'createdAt', header: 'Quando', render: (r) => <span className="text-xs text-ink-muted">{fmt.dateTime(r.createdAt)}</span> },
    { key: 'method', header: 'Metodo', render: (r) => <MethodBadge method={r.method} /> },
    { key: 'path', header: 'Path', render: (r) => <code className="text-xs">{r.path}</code> },
    { key: 'statusCode', header: 'Stato', render: (r) => <Badge tone={statusTone(r.statusCode)}>{r.statusCode}</Badge> },
    { key: 'durationMs', header: 'Latenza', align: 'right', render: (r) => fmt.durationMs(r.durationMs) },
    { key: 'principal', header: 'Principal', render: (r) => r.principal?.email || r.principal?.id || <span className="text-ink-faint">-</span> },
    { key: 'requestId', header: 'Request', render: (r) => <RequestId value={r.requestId} /> },
    { key: 'actions', header: '', align: 'right', render: (r) => <DetailButton onClick={() => setDetail(r)} /> }
  ];

  return (
    <>
      <FilterBar>
        <Input className="w-64" label="Request ID" value={filters.requestId || ''} onChange={(e) => setFilters({ requestId: e.target.value || undefined })} />
        <Input className="w-40" label="Status code" type="number" value={filters.statusCode || ''} onChange={(e) => setFilters({ statusCode: e.target.value || undefined })} />
      </FilterBar>
      <DataTable columns={columns} rows={items} loading={isLoading} empty={{ title: 'Nessuna richiesta loggata' }} />
      <Pagination pagination={pagination} onPageChange={setPage} />
      <DetailModal open={Boolean(detail)} onClose={() => setDetail(null)} title="Richiesta" payload={detail} />
    </>
  );
}

function ErrorsTab() {
  const { items, isLoading, pagination, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['logs', 'errors'],
    fetcher: logsApi.listErrors
  });
  const [detail, setDetail] = useState(null);

  const columns = [
    { key: 'createdAt', header: 'Quando', render: (r) => <span className="text-xs text-ink-muted">{fmt.dateTime(r.createdAt)}</span> },
    { key: 'code', header: 'Codice', render: (r) => <code className="text-xs">{r.code}</code> },
    { key: 'statusCode', header: 'HTTP', render: (r) => <Badge tone={statusTone(r.statusCode)}>{r.statusCode}</Badge> },
    { key: 'message', header: 'Messaggio', render: (r) => <span className="line-clamp-2 text-sm">{r.message}</span> },
    { key: 'principal', header: 'Principal', render: (r) => r.principal?.email || r.principal?.id || <span className="text-ink-faint">-</span> },
    { key: 'requestId', header: 'Request', render: (r) => <RequestId value={r.requestId} /> },
    { key: 'actions', header: '', align: 'right', render: (r) => <DetailButton onClick={() => setDetail(r)} /> }
  ];

  return (
    <>
      <FilterBar>
        <Input className="w-64" label="Codice errore" value={filters.code || ''} onChange={(e) => setFilters({ code: e.target.value || undefined })} />
        <Input className="w-64" label="Request ID" value={filters.requestId || ''} onChange={(e) => setFilters({ requestId: e.target.value || undefined })} />
      </FilterBar>
      <DataTable columns={columns} rows={items} loading={isLoading} empty={{ title: 'Nessun errore registrato' }} />
      <Pagination pagination={pagination} onPageChange={setPage} />
      <DetailModal open={Boolean(detail)} onClose={() => setDetail(null)} title="Errore" payload={detail} />
    </>
  );
}

function AuditTab() {
  const { items, isLoading, pagination, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['logs', 'audit'],
    fetcher: logsApi.listAudit
  });
  const facets = useQuery({ queryKey: ['logs', 'audit', 'facets'], queryFn: logsApi.auditFacets });
  const actionOptions = facets.data?.actions || [];
  const targetTypeOptions = facets.data?.targetTypes || [];
  const [detail, setDetail] = useState(null);

  const columns = [
    { key: 'createdAt', header: 'Quando', render: (r) => <span className="text-xs text-ink-muted">{fmt.dateTime(r.createdAt)}</span> },
    { key: 'action', header: 'Azione', render: (r) => <span className="text-sm text-ink" title={r.action}>{humanizeCode(r.action)}</span> },
    { key: 'severity', header: 'Severità', render: (r) => <Badge tone={severityTone(r.severity)}>{r.severity}</Badge> },
    { key: 'actor', header: 'Attore', render: (r) => r.actor?.email || r.actor?.id || r.actor?.kind || '-' },
    { key: 'target', header: 'Target', render: (r) => r.target ? (
      <span className="text-xs" title={`${r.target.type}/${r.target.id || ''}`}>
        <span className="text-ink">{humanizeCode(r.target.type)}</span>
        {(r.target.label || r.target.id) && <span className="text-ink-muted"> · {r.target.label || r.target.id}</span>}
      </span>
    ) : '-' },
    { key: 'actions', header: '', align: 'right', render: (r) => <DetailButton onClick={() => setDetail(r)} /> }
  ];

  return (
    <>
      <FilterBar>
        <Select label="Azione" className="w-64" value={filters.action || ''} onChange={(e) => setFilters({ action: e.target.value || undefined })}>
          <option value="">Tutte</option>
          {actionOptions.map((a) => <option key={a} value={a}>{humanizeCode(a)}</option>)}
        </Select>
        <Select label="Severità" className="w-40" value={filters.severity || ''} onChange={(e) => setFilters({ severity: e.target.value || undefined })}>
          <option value="">Tutte</option>
          <option value="info">Info</option>
          <option value="notice">Notice</option>
          <option value="warning">Warning</option>
          <option value="critical">Critical</option>
        </Select>
        <Select label="Tipo target" className="w-48" value={filters.targetType || ''} onChange={(e) => setFilters({ targetType: e.target.value || undefined })}>
          <option value="">Tutti</option>
          {targetTypeOptions.map((tt) => <option key={tt} value={tt}>{humanizeCode(tt)}</option>)}
        </Select>
      </FilterBar>
      <DataTable columns={columns} rows={items} loading={isLoading} empty={{ title: 'Nessuna voce di audit' }} />
      <Pagination pagination={pagination} onPageChange={setPage} />
      <DetailModal open={Boolean(detail)} onClose={() => setDetail(null)} title="Audit" payload={detail} />
    </>
  );
}

function AuthAuditTab() {
  const { items, isLoading, pagination, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['logs', 'auth-audit'],
    fetcher: logsApi.listAuthAudit
  });
  const facets = useQuery({ queryKey: ['logs', 'auth-audit', 'facets'], queryFn: logsApi.authAuditFacets });
  const eventOptions = facets.data?.events || [];
  const [detail, setDetail] = useState(null);

  const columns = [
    { key: 'createdAt', header: 'Quando', render: (r) => <span className="text-xs text-ink-muted">{fmt.dateTime(r.createdAt)}</span> },
    { key: 'event', header: 'Evento', render: (r) => <span className="text-sm text-ink" title={r.event}>{humanizeCode(r.event)}</span> },
    { key: 'success', header: 'Esito', render: (r) => r.success ? <Badge tone="success">ok</Badge> : <Badge tone="danger">fallito</Badge> },
    { key: 'emailAttempted', header: 'Email', render: (r) => <span className="text-xs">{r.emailAttempted || r.userId || '-'}</span> },
    { key: 'ip', header: 'IP', render: (r) => <span className="text-xs text-ink-muted">{r.ip || '-'}</span> },
    { key: 'failureReason', header: 'Motivo fallimento', render: (r) => <span className="text-xs text-ink-muted">{r.failureReason ? humanizeCode(r.failureReason) : '-'}</span> },
    { key: 'actions', header: '', align: 'right', render: (r) => <DetailButton onClick={() => setDetail(r)} /> }
  ];

  return (
    <>
      <FilterBar>
        <Select label="Evento" className="w-56" value={filters.event || ''} onChange={(e) => setFilters({ event: e.target.value || undefined })}>
          <option value="">Tutti</option>
          {eventOptions.map((ev) => <option key={ev} value={ev}>{humanizeCode(ev)}</option>)}
        </Select>
        <Input className="w-64" label="Email" value={filters.emailAttempted || ''} onChange={(e) => setFilters({ emailAttempted: e.target.value || undefined })} />
        <Input className="w-44" label="IP" value={filters.ip || ''} onChange={(e) => setFilters({ ip: e.target.value || undefined })} />
        <Select label="Esito" className="w-40" value={filters.success === undefined ? '' : String(filters.success)} onChange={(e) => {
          const v = e.target.value;
          setFilters({ success: v === '' ? undefined : v === 'true' });
        }}>
          <option value="">Tutti</option>
          <option value="true">Successo</option>
          <option value="false">Fallito</option>
        </Select>
      </FilterBar>
      <DataTable columns={columns} rows={items} loading={isLoading} empty={{ title: 'Nessuna voce di auth audit' }} />
      <Pagination pagination={pagination} onPageChange={setPage} />
      <DetailModal open={Boolean(detail)} onClose={() => setDetail(null)} title="Auth audit" payload={detail} />
    </>
  );
}

function RequestLookup() {
  const [rid, setRid] = useState('');
  const [result, setResult] = useState(null);

  const lookup = useMutation({
    mutationFn: () => logsApi.byRequestId(rid.trim()),
    onSuccess: setResult,
    onError: (err) => { setResult(null); toast.error(err.message || t('common.error')); }
  });

  return (
    <Card padded={false} className="p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-2">
          <Search className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden="true" />
          <div>
            <h2 className="text-sm font-semibold text-ink">Lookup per Request ID</h2>
            <p className="text-xs text-ink-muted">Tutti i log correlati allo stesso requestId.</p>
          </div>
        </div>
        <div className="flex items-end gap-2">
          <Input
            className="w-full sm:w-72"
            value={rid}
            onChange={(e) => setRid(e.target.value)}
            placeholder="es. 019e6f1e-..."
            onKeyDown={(e) => { if (e.key === 'Enter' && rid.trim()) lookup.mutate(); }}
          />
          <Button onClick={() => lookup.mutate()} loading={lookup.isPending} disabled={!rid.trim()}>Cerca</Button>
        </div>
      </div>
      {result && (
        <pre className="mt-3 max-h-72 overflow-auto rounded-md bg-surface p-3 text-xs">{JSON.stringify(result, null, 2)}</pre>
      )}
    </Card>
  );
}

const TABS_ALL = [
  { value: 'requests', label: 'Richieste', perm: 'logs:read' },
  { value: 'errors', label: 'Errori', perm: 'logs:read' },
  { value: 'audit', label: 'Audit business', perm: 'audit:read' },
  { value: 'authAudit', label: 'Audit autenticazioni', perm: 'auth-audit:read' }
];

export function LogsPage() {
  const { hasPermission } = useAuth();
  const tabs = TABS_ALL.filter((tab) => hasPermission(tab.perm));
  const [tab, setTab] = useState(tabs[0]?.value || 'requests');

  if (tabs.length === 0) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t('nav.logs')} />
        <Card><p className="text-sm text-ink-muted">Nessun permesso di lettura log.</p></Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('nav.logs')} description="Tracciato richieste, errori, audit di business e di autenticazione." />
      {hasPermission('logs:read') && <RequestLookup />}
      <div className="flex flex-col gap-4">
        <Tabs value={tab} onChange={setTab} items={tabs.map(({ value, label }) => ({ value, label }))} />
        {tab === 'requests' && <RequestsTab />}
        {tab === 'errors' && <ErrorsTab />}
        {tab === 'audit' && <AuditTab />}
        {tab === 'authAudit' && <AuthAuditTab />}
      </div>
    </div>
  );
}
